import { useEffect, useState } from 'react';
import { fetchCampaignRequestPossibleMatches } from '../../../services/campaign';

interface PossibleMatch {
  campaign_id: number;
  name: string;
  email: string;
  address: string;
  medical_reason: string;
  phone: string;
  status: string;
  match_type: 'email' | 'name' | 'address' | 'phone';
  ntaf_id: string;
  email_match_type?: 'primary_contact' | 'client';
  matched_email?: string;
}

interface RequestMatch {
  campaign_request_id: number;
  name: string;
  email: string;
  address: string;
  medical_reason: string;
  phone: string;
  status: string;
  match_type: 'email' | 'name' | 'address' | 'phone';
  created_date: string;
}

interface Props {
  campaignRequest: any;
  preLoadedMatches?: PossibleMatch[];
  preLoadedRequestMatches?: RequestMatch[];
}

const PossibleMatches: React.FC<Props> = ({ campaignRequest, preLoadedMatches, preLoadedRequestMatches }) => {
  const [matches, setMatches] = useState<PossibleMatch[]>(preLoadedMatches || []);
  const [requestMatches, setRequestMatches] = useState<RequestMatch[]>(preLoadedRequestMatches || []);
  const [loading, setLoading] = useState(!preLoadedMatches);
  const [error, setError] = useState<string | null>(null);

  // Get current campaign request data for comparison
  const currentEmail = campaignRequest?.patient_email || campaignRequest?.initial_email || '';
  const currentFirstName = campaignRequest?.patient_first_name || campaignRequest?.initial_first_name || '';
  const currentLastName = campaignRequest?.patient_last_name || campaignRequest?.initial_last_name || '';
  const currentAddress = [
    campaignRequest?.patient_address_1,
    campaignRequest?.patient_city,
    campaignRequest?.patient_state?.state || campaignRequest?.patient_state?.abbreviation || '',
    campaignRequest?.patient_zip_code
  ].filter(Boolean).join(', ');
  const currentPhone = campaignRequest?.initial_phone || campaignRequest?.patient_phone1 || campaignRequest?.patient_phone2 || '';

  // Debug logging
  console.log('Current campaign request data:', {
    campaignRequest,
    currentEmail,
    currentFirstName,
    currentLastName,
    currentAddress,
    currentPhone,
    patientState: campaignRequest?.patient_state
  });
  console.log('Matches data:', matches);


  useEffect(() => {
    // If we have pre-loaded matches, use them and don't fetch again
    if (preLoadedMatches) {
      setMatches(preLoadedMatches);
      setRequestMatches(preLoadedRequestMatches || []);
      setLoading(false);
      return;
    }

    // Otherwise, fetch the matches
    const loadMatches = async () => {
      if (!campaignRequest?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        const data = await fetchCampaignRequestPossibleMatches(campaignRequest.id);
        if (data.matches) {
          setMatches(data.matches || []);
          setRequestMatches(data.request_matches || []);
        } else {
          setMatches(data || []);
          setRequestMatches([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load possible matches');
        console.error('Error fetching possible matches:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMatches();
  }, [campaignRequest?.id, preLoadedMatches, preLoadedRequestMatches]);

  const isEmailMatch = (matchEmail: string) => {
    if (!currentEmail || !matchEmail) return false;
    const result = currentEmail.toLowerCase() === matchEmail.toLowerCase();
    console.log('Email match check:', { currentEmail, matchEmail, result });
    return result;
  };

  const isNameMatch = (matchName: string) => {
    const currentFullName = `${currentFirstName} ${currentLastName}`.trim();
    const result = currentFullName && matchName && currentFullName.toLowerCase() === matchName.toLowerCase();
    console.log('Name match check:', { currentFirstName, currentLastName, currentFullName, matchName, result });
    return result;
  };

  const isAddressMatch = (matchAddress: string) => {
    const result = currentAddress && matchAddress && currentAddress.toLowerCase() === matchAddress.toLowerCase();
    console.log('Address match check:', { currentAddress, matchAddress, result });
    return result;
  };

  const isPhoneMatch = (matchPhone: string) => {
    if (!currentPhone || !matchPhone) return false;
    // Compare phone numbers after removing any formatting
    const cleanCurrentPhone = currentPhone.replace(/\D/g, '');
    const cleanMatchPhone = matchPhone.replace(/\D/g, '');
    const result = cleanCurrentPhone === cleanMatchPhone;
    console.log('Phone match check:', { currentPhone, matchPhone, cleanCurrentPhone, cleanMatchPhone, result });
    return result;
  };

  const getMatchClass = (isMatch: boolean, fieldName: string) => {
    const className = isMatch ? 'font-semibold !text-red-600 !bg-red-50 px-2 py-1 rounded' : 'text-gray-900';
    console.log(`${fieldName} class:`, { isMatch, className });
    return className;
  };

  const getMatchStyle = (isMatch: boolean, fieldName: string) => {
    const style = isMatch ? {
      fontWeight: 'bold',
      color: '#dc2626', // red-600
      backgroundColor: '#fef2f2', // red-50
      padding: '4px 8px',
      borderRadius: '4px'
    } : {};
    console.log(`${fieldName} style:`, { isMatch, style });
    return style;
  };

  const getMatchTypeColor = (matchType: string) => {
    switch (matchType) {
      case 'email':
        return 'text-red-600 bg-red-50';
      case 'phone':
        return 'text-purple-600 bg-purple-50';
      case 'name':
        return 'text-yellow-600 bg-yellow-50';
      case 'address':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getMatchTypeLabel = (matchType: string) => {
    switch (matchType) {
      case 'email':
        return 'Email Match';
      case 'phone':
        return 'Phone Match';
      case 'name':
        return 'Name Match';
      case 'address':
        return 'Address Match';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading possible matches...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading matches</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (matches.length === 0 && requestMatches.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
          <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No possible matches found</h3>
        <p className="mt-1 text-sm text-gray-500">
          No existing campaigns or other campaign requests match the email, name, or address for this request.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {matches.length > 0 && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Possible Existing Campaigns</h3>
                <div className="mt-2 text-sm text-blue-700">
                  Found {matches.length} possible campaign match{matches.length !== 1 ? 'es' : ''} based on email, name, and address data.
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Medical Reason
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Match Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {matches.map((match, index) => (
                  <tr
                    key={`${match.campaign_id}-${index}`}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => window.open(`/campaigns/${match.campaign_id}`, '_blank')}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <span 
                        className={getMatchClass(isNameMatch(match.name), 'name')}
                        style={getMatchStyle(isNameMatch(match.name), 'name')}
                      >
                        {match.name || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span 
                        className={getMatchClass(isEmailMatch(match.email), 'email')}
                        style={getMatchStyle(isEmailMatch(match.email), 'email')}
                      >
                        {match.email || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span 
                        className={getMatchClass(isAddressMatch(match.address), 'address')}
                        style={getMatchStyle(isAddressMatch(match.address), 'address')}
                      >
                        {match.address || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {match.medical_reason || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span 
                        className={getMatchClass(isPhoneMatch(match.phone), 'phone')}
                        style={getMatchStyle(isPhoneMatch(match.phone), 'phone')}
                      >
                        {match.phone || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {match.status || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMatchTypeColor(match.match_type)}`}>
                        {getMatchTypeLabel(match.match_type)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {requestMatches.length > 0 && (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">Other Campaign Requests</h3>
                <div className="mt-2 text-sm text-amber-700">
                  Found {requestMatches.length} other campaign request{requestMatches.length !== 1 ? 's' : ''} with matching client information. Check the Status column &mdash; previously rejected requests are included.
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Medical Reason
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Match Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requestMatches.map((match, index) => (
                  <tr
                    key={`cr-${match.campaign_request_id}-${index}`}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => window.open(`/campaign-requests/${match.campaign_request_id}`, '_blank')}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <span 
                        className={getMatchClass(isNameMatch(match.name), 'name')}
                        style={getMatchStyle(isNameMatch(match.name), 'name')}
                      >
                        {match.name || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span 
                        className={getMatchClass(isEmailMatch(match.email), 'email')}
                        style={getMatchStyle(isEmailMatch(match.email), 'email')}
                      >
                        {match.email || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span 
                        className={getMatchClass(isAddressMatch(match.address), 'address')}
                        style={getMatchStyle(isAddressMatch(match.address), 'address')}
                      >
                        {match.address || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {match.medical_reason || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span 
                        className={getMatchClass(isPhoneMatch(match.phone), 'phone')}
                        style={getMatchStyle(isPhoneMatch(match.phone), 'phone')}
                      >
                        {match.phone || ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {match.status || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMatchTypeColor(match.match_type)}`}>
                        {getMatchTypeLabel(match.match_type)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PossibleMatches;
